// In src/pages/ContactPage.js

import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

// This Section matches the style of your other pages
const Section = styled(motion.section)`
  min-height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${(props) => props.theme.body};
  color: ${(props) => props.theme.text};
  position: relative;
  padding: 5rem 0;
`;

// Title style from your Banner.js
const Title = styled(motion.h1)`
  font-size: ${(props) => props.theme.fontxxxl};
  font-family: "Kaushan Script";
  font-weight: 300;
  color: ${(props) => props.theme.text};
  text-shadow: 1px 1px 1px ${(props) => props.theme.text};
  margin-bottom: 2rem;

  @media (max-width: 70em) {
    font-size: ${(props) => props.theme.fontxxl};
  }
  @media (max-width: 48em) {
    font-size: ${(props) => props.theme.fontxl};
  }
`;

// A container for the form
const Form = styled(motion.form)`
  display: flex;
  flex-direction: column;
  width: 50vw;

  @media (max-width: 64em) {
    width: 70vw;
  }
  @media (max-width: 48em) {
    width: 90vw;
  }
`;

// Styled container for each field (Label + Input)
const FormField = styled(motion.div)`
  display: flex;
  flex-direction: column;
  margin-bottom: 1.5rem;

  label {
    font-size: ${(props) => props.theme.fontlg};
    margin-bottom: 0.5rem;
    color: ${(props) => props.theme.text};
  }

  input,
  textarea {
    font-size: ${(props) => props.theme.fontmd};
    padding: 1rem;
    background-color: ${(props) => props.theme.text};
    color: ${(props) => props.theme.body};
    border: 1px solid ${(props) => props.theme.text};
    border-radius: 5px;

    &::placeholder {
      color: ${(props) => `rgba(${props.theme.bodyRgba}, 0.6)`};
    }

    &:focus {
      outline: 2px solid ${(props) => props.theme.body};
      border-color: ${(props) => props.theme.body};
    }
  }

  textarea {
    min-height: 150px;
    resize: vertical;
  }
`;

// Styled button
const SubmitButton = styled(motion.button)`
  font-size: ${(props) => props.theme.fontlg};
  font-weight: 600;
  padding: 1rem 2rem;
  background-color: ${(props) => props.theme.text};
  color: ${(props) => props.theme.body};
  border: 1px solid ${(props) => props.theme.text};
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;

  &:hover {
    background-color: ${(props) => props.theme.body};
    color: ${(props) => props.theme.text};
    border-color: ${(props) => props.theme.text};
  }
  
  &:disabled {
    background-color: #888;
    cursor: not-allowed;
  }
`;

// Message for success or error
const ResultMessage = styled(motion.div)`
  font-size: ${(props) => props.theme.fontlg};
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 5px;
  text-align: center;
  
  &.success {
    background-color: #4CAF50; // Green
    color: white;
  }
  
  &.error {
    background-color: #f44336; // Red
    color: white;
  }
`;

// Animation variants for Framer Motion
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 }
};


const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    details: "",
  });
  const [status, setStatus] = useState(""); // "", "submitting", "success", "error"

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");

    const formDataWeb3 = new FormData();
    // Your web3forms access key
    formDataWeb3.append("access_key", "1b282fa4-a7e3-446b-b8fb-e9be0b25a268");
    formDataWeb3.append("name", formData.name);
    formDataWeb3.append("email", formData.email);
    formDataWeb3.append("phone", formData.phone);
    formDataWeb3.append("details", formData.details);
    formDataWeb3.append("subject", `New Contact from ${formData.name}`);

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formDataWeb3,
      });
      const json = await res.json();

      if (json.success) {
        setStatus("success");
        setFormData({ name: "", email: "", phone: "", details: "" }); // Clear form
      } else {
        console.error("web3forms error:", json.message);
        setStatus("error");
      }
    } catch (err) {
      console.error("Submission error:", err);
      setStatus("error");
    }
  };

  return (
    <Section
      // Page transition animation
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Title
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        Get In Touch
      </Title>
      
      <Form
        onSubmit={handleSubmit}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <FormField variants={itemVariants}>
          <label htmlFor="name">Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Your Name"
            required
          />
        </FormField>
        
        <FormField variants={itemVariants}>
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your.email@example.com"
            required
          />
        </FormField>
        
        <FormField variants={itemVariants}>
          <label htmlFor="phone">Phone (Optional)</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+1 234 567 890"
          />
        </FormField>
        
        <FormField variants={itemVariants}>
          <label htmlFor="details">Details</label>
          <textarea
            id="details"
            name="details"
            value={formData.details}
            onChange={handleChange}
            placeholder="How can we help you?"
            required
          />
        </FormField>
        
        <SubmitButton
          type="submit"
          disabled={status === "submitting"}
          variants={itemVariants}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {status === "submitting" ? "Sending..." : "Send Message"}
        </SubmitButton>
        
        {status === "success" && (
          <ResultMessage
            className="success"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            Message sent! We'll get back to you soon.
          </ResultMessage>
        )}
        {status === "error" && (
          <ResultMessage
            className="error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            Something went wrong. Please try again.
          </ResultMessage>
        )}
      </Form>
    </Section>
  );
};

export default ContactPage;